import { getHandoverPatients } from './handoverUtils';

const SEVERITY_ORDER = ['critical', 'high_alert', 'concern', 'warning', 'verify'];

const VITAL_KEY_ALIASES = {
  bp: ['bp', 'blood pressure', 'systolic', 'diastolic', 'mmhg', 'hypertension', 'hypotension', 'hypertensive'],
  pulse: ['pulse', 'heart rate', 'hr', 'bpm', 'tachycardia', 'bradycardia'],
  temp_celsius: ['temp', 'temperature', 'celsius', '°c', 'fever', 'febrile', 'hypothermia'],
  o2_sat: ['o2', 'oxygen', 'saturation', 'spo2', 'hypoxia', 'hypoxemia', 'desaturation'],
  rr: ['rr', 'respiratory', 'respiration', 'breaths', 'tachypnea', 'bradypnea'],
  pain: ['pain'],
  weight_kg: ['weight', 'kg'],
  blood_sugar: ['blood sugar', 'glucose', 'bg', 'sugar', 'bgl', 'rbs', 'hypoglycemia', 'hyperglycemia'],
};

const ACTIONABLE_TERMS = [
  'notify',
  'notified',
  'urgent',
  'immediately',
  'monitor',
  'reassess',
  'verify',
  'confirm',
  'withhold',
  'hold',
  'escalate',
  'physician',
  'doctor',
  'action',
  'intervene',
  'intervention',
  'repeat',
  'recheck',
];

function inferSeverityFromString(text) {
  const lower = text.toLowerCase();
  if (lower.includes('high alert')) return 'high_alert';
  if (lower.includes('adverse reaction')) return 'critical';
  if (lower.includes('please verify')) return 'verify';
  if (lower.includes('concern')) return 'concern';
  if (lower.includes('warning') || lower.includes('abnormal')) return 'warning';
  if (lower.includes('critical')) return 'critical';
  return 'verify';
}

function inferCategoryFromString(text) {
  const lower = text.toLowerCase();
  if (lower.includes('medication') || lower.includes('high alert') || lower.includes('drug')) {
    return 'medication';
  }
  if (lower.includes('adverse reaction')) return 'adverse_reaction';
  if (lower.includes('patient name') || lower.includes('room number')) return 'documentation';
  if (lower.includes('bp') || lower.includes('pulse') || lower.includes('vital')) return 'vitals';
  return 'documentation';
}

function flagMessageText(flag) {
  if (!flag) return '';
  if (typeof flag === 'string') return flag;
  return flag.message || flag.text || flag.value || '';
}

function normalizeText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s./-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function normalizeValueToken(value) {
  return normalizeText(value)
    .replace(/\s*\/\s*/g, '/')
    .replace(/\s+/g, '');
}

function extractNumericTokens(text) {
  const matches = String(text || '').match(/\d+(?:\.\d+)?(?:\s*\/\s*\d+(?:\.\d+)?)?/g);
  if (!matches?.length) return '';
  return matches.map((part) => normalizeValueToken(part)).join('|');
}

function detectVitalType(text) {
  const haystack = normalizeText(text);
  for (const [key, aliases] of Object.entries(VITAL_KEY_ALIASES)) {
    if (aliases.some((alias) => haystack.includes(alias))) {
      return key;
    }
  }
  return null;
}

function extractMedicationKey(flag) {
  const valueText = normalizeText(flag.value);
  const messageText = normalizeText(flag.message);
  const source = valueText || messageText;
  if (!source) return null;

  const skip = new Set([
    'mg', 'mcg', 'g', 'ml', 'po', 'iv', 'im', 'subcut', 'tablet', 'capsule', 'dose',
    'high', 'alert', 'medication', 'administered', 'beta', 'blocker', 'ensure', 'monitoring',
    'verify', 'unclear', 'route', 'drug',
  ]);
  const token = source
    .split(' ')
    .find((part) => part.length >= 3 && !skip.has(part) && !/^\d/.test(part));
  return token || source;
}

function actionabilityScore(message) {
  const text = normalizeText(message);
  return ACTIONABLE_TERMS.reduce((score, term) => (text.includes(term) ? score + 1 : score), 0);
}

function specificityScore(flag) {
  let score = 0;
  if (flag.value) score += 4;
  score += actionabilityScore(flag.message) * 2;
  score += Math.min(normalizeText(flag.message).split(' ').filter(Boolean).length, 24);
  if (/\d/.test(flag.message || '')) score += 2;
  return score;
}

function severityRank(severity) {
  const index = SEVERITY_ORDER.indexOf(severity);
  return index === -1 ? SEVERITY_ORDER.length : index;
}

/** Prefer lower severity rank, then higher specificity/actionability. */
export function isPreferableClinicalFlag(candidate, incumbent) {
  if (!incumbent) return true;
  const severityDelta = severityRank(candidate.severity) - severityRank(incumbent.severity);
  if (severityDelta !== 0) return severityDelta < 0;
  return specificityScore(candidate) >= specificityScore(incumbent);
}

/**
 * Grouping key for the same clinical finding.
 * Vital flags with the same vital type + value collapse together.
 */
export function getClinicalFindingKey(flag) {
  const category = flag.category || 'documentation';
  const message = normalizeText(flag.message);
  const value = normalizeValueToken(flag.value);
  const combined = `${value} ${message}`.trim();

  if (category === 'vitals' || detectVitalType(combined)) {
    const vitalType = detectVitalType(combined) || 'vital';
    const valueKey = value || extractNumericTokens(combined) || message;
    return `vitals|${vitalType}|${valueKey}`;
  }

  if (category === 'medication' || category === 'adverse_reaction') {
    const medKey = extractMedicationKey(flag) || message;
    return `${category}|${medKey}|${flag.severity || 'verify'}`;
  }

  if (category === 'negation_conflict') {
    return `negation_conflict|${message}`;
  }

  const compactMessage = message
    .replace(/\b(please|urgently|immediately|noted|documented)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `${category}|${compactMessage || message}|${value}`;
}

function messagesAreNearDuplicates(a, b) {
  const left = normalizeText(a);
  const right = normalizeText(b);
  if (!left || !right) return false;
  if (left === right) return true;
  if (left.includes(right) || right.includes(left)) return true;

  const leftTokens = new Set(left.split(' ').filter((token) => token.length > 2));
  const rightTokens = new Set(right.split(' ').filter((token) => token.length > 2));
  if (!leftTokens.size || !rightTokens.size) return false;

  let overlap = 0;
  leftTokens.forEach((token) => {
    if (rightTokens.has(token)) overlap += 1;
  });
  const similarity = overlap / Math.min(leftTokens.size, rightTokens.size);
  return similarity >= 0.75;
}

/**
 * Keep one flag per clinical finding — the most severe, then most specific/actionable.
 * Also collapses duplicate vital flags that share the same vital value.
 */
export function deduplicateClinicalFlags(flags = []) {
  const normalized = (Array.isArray(flags) ? flags : [flags])
    .map(normalizeClinicalFlag)
    .filter(Boolean)
    .filter((flag) => !isSpuriousDocumentationFlag(flag));

  const byFinding = new Map();

  normalized.forEach((flag) => {
    const key = getClinicalFindingKey(flag);
    const existing = byFinding.get(key);
    if (!existing || isPreferableClinicalFlag(flag, existing)) {
      byFinding.set(key, flag);
    }
  });

  // Second pass: merge near-duplicate messages that got different keys.
  const retained = [...byFinding.values()];
  const merged = [];

  retained.forEach((flag) => {
    const matchIndex = merged.findIndex((other) => {
      if (other.category !== flag.category) return false;
      if (other.category === 'vitals' || flag.category === 'vitals') {
        const aType = detectVitalType(`${other.value || ''} ${other.message || ''}`);
        const bType = detectVitalType(`${flag.value || ''} ${flag.message || ''}`);
        const aValue =
          normalizeValueToken(other.value)
          || extractNumericTokens(`${other.value || ''} ${other.message || ''}`);
        const bValue =
          normalizeValueToken(flag.value)
          || extractNumericTokens(`${flag.value || ''} ${flag.message || ''}`);
        if (!aValue || aValue !== bValue) return false;
        // Same vital value collapses unless both sides clearly name different vital types.
        if (aType && bType && aType !== bType) return false;
        return true;
      }
      return messagesAreNearDuplicates(other.message, flag.message);
    });

    if (matchIndex === -1) {
      merged.push(flag);
      return;
    }

    if (isPreferableClinicalFlag(flag, merged[matchIndex])) {
      merged[matchIndex] = flag;
    }
  });

  return sortFlags(merged);
}

const SPURIOUS_DOCUMENTATION_FLAG_PATTERNS = [
  'patient identifier missing',
  'patient identification not documented',
  'patient location not documented',
  'identification not documented',
  'location not documented',
  'room/bed missing',
  'room bed missing',
  'no name or room/bed provided',
  'no name or room provided',
  'patient details not provided',
  'obtain and document the patient identifier',
  'confirm and document patient identifier',
  'please obtain and document',
  'patient name not detected',
  'patient name not identified',
  'patient name not documented',
  'patient name missing',
  'room number not detected',
  'room number not documented',
  'room number missing',
  'room not identified',
  'room not documented',
  'room/bed not documented',
  'verify identifiers if required',
  'identifiers if required for documentation',
  'patient age, and patient sex not provided',
  'patient age and patient sex not provided',
  'room number, patient age, and patient sex',
  'patient age not provided',
  'patient sex not provided',
  'room number not provided in transcript',
  'age not provided in transcript',
  'sex not provided in transcript',
];

function isMissingDemographicsDocumentationFlag(text) {
  const lower = String(text || '').toLowerCase();
  if (!lower) return false;
  const mentionsMissingDemo =
    (lower.includes('room') || lower.includes('age') || lower.includes('sex'))
    && (
      lower.includes('not provided')
      || lower.includes('not documented')
      || lower.includes('missing')
      || lower.includes('not detected')
    );
  const isIdentifierNudge =
    lower.includes('verify identifier')
    || lower.includes('verify identifiers')
    || lower.includes('patient identifier')
    || lower.includes('for documentation');
  return mentionsMissingDemo && (isIdentifierNudge || lower.includes('transcript'));
}

export function isSpuriousDocumentationFlag(flag) {
  const text = flagMessageText(flag).toLowerCase();
  if (!text) return false;
  if (SPURIOUS_DOCUMENTATION_FLAG_PATTERNS.some((pattern) => text.includes(pattern))) {
    return true;
  }
  // Model often flags missing room/age/sex even when patient name was extracted.
  if (isMissingDemographicsDocumentationFlag(text)) return true;
  return false;
}

export function filterDisplayClinicalFlags(flags = []) {
  return deduplicateClinicalFlags(flags);
}

export function normalizeClinicalFlag(flag) {
  if (!flag) return null;
  if (typeof flag === 'string') {
    return {
      severity: inferSeverityFromString(flag),
      category: inferCategoryFromString(flag),
      message: flag,
      value: null,
    };
  }

  return {
    severity: flag.severity || 'verify',
    category: flag.category || 'documentation',
    message: flag.message || String(flag),
    value: flag.value ?? null,
  };
}

export function normalizeClinicalFlags(flags = []) {
  return deduplicateClinicalFlags(
    (Array.isArray(flags) ? flags : [flags])
      .map(normalizeClinicalFlag)
      .filter(Boolean)
  );
}

export function collectNoteFlags(note) {
  if (!note) return [];
  const flags = [...normalizeClinicalFlags(note.flags)];

  if (note.note_type === 'handover' || note.patients?.length) {
    getHandoverPatients(note).forEach((entry) => {
      flags.push(...normalizeClinicalFlags(entry.flags));
    });
  }

  return deduplicateClinicalFlags(flags);
}

export function collectFlagsFromNotes(notes = []) {
  const all = [];
  notes.forEach((note) => {
    all.push(...collectNoteFlags(note));
  });
  return deduplicateClinicalFlags(all);
}

function flagMatchesText(flag, ...parts) {
  const haystack = `${flag.value || ''} ${flag.message || ''}`.toLowerCase();
  return parts
    .filter(Boolean)
    .some((part) => haystack.includes(String(part).toLowerCase()));
}

export function getVitalFlagSeverity(flags, vitalKey, displayValue) {
  const vitalFlags = normalizeClinicalFlags(flags).filter((flag) => flag.category === 'vitals');
  const aliases = VITAL_KEY_ALIASES[vitalKey] || [vitalKey];

  for (const flag of vitalFlags) {
    const keyMatch = aliases.some((alias) => flagMatchesText(flag, alias));
    const valueMatch = displayValue && flagMatchesText(flag, displayValue);
    if (keyMatch || valueMatch) {
      if (flag.severity === 'critical') return 'critical';
      if (flag.severity === 'warning') return 'warning';
    }
  }

  return null;
}

export function getMedicationFlagState(flags, drugName) {
  const medFlags = normalizeClinicalFlags(flags).filter((flag) => flag.category === 'medication');
  const relevant = medFlags.filter((flag) => flagMatchesText(flag, drugName));

  return {
    highAlert: relevant.some((flag) => flag.severity === 'high_alert'),
    verify: relevant.some((flag) => flag.severity === 'verify'),
    verified: relevant.length === 0,
  };
}

export function getVitalCardClasses(severity) {
  if (severity === 'critical') {
    return 'rounded-xl border border-[#DC2626] bg-[#FEF2F2] p-3';
  }
  if (severity === 'warning') {
    return 'rounded-xl border border-[#D97706] bg-[#FEF3C7] p-3';
  }
  return 'rounded-xl border border-gray-200 bg-white p-3 shadow-sm';
}

export function getVitalValueClass(severity) {
  if (severity === 'critical') return 'text-[#DC2626]';
  if (severity === 'warning') return 'text-[#D97706]';
  return 'text-gray-900';
}

export function getVitalCellColorClass(severity) {
  if (severity === 'critical') return 'text-[#DC2626] font-semibold';
  if (severity === 'warning') return 'text-[#D97706] font-semibold';
  return 'text-gray-900';
}

export function groupFlagsBySeverity(flags = []) {
  const normalized = deduplicateClinicalFlags(flags);
  const groups = {
    critical: [],
    high_alert: [],
    concern: [],
    warning: [],
    verify: [],
  };

  normalized.forEach((flag) => {
    const bucket = groups[flag.severity] ? flag.severity : 'verify';
    groups[bucket].push(flag);
  });

  groups.urgent = [...groups.critical, ...groups.high_alert];
  return groups;
}

export function hasAdverseReactionFlag(flags) {
  return normalizeClinicalFlags(flags).some(
    (flag) => flag.category === 'adverse_reaction' && flag.severity === 'critical'
  );
}

export function getAdverseReactionFlags(flags) {
  return normalizeClinicalFlags(flags).filter(
    (flag) => flag.category === 'adverse_reaction' && flag.severity === 'critical'
  );
}

export function hasHighAlertMedicationFlag(flags) {
  return normalizeClinicalFlags(flags).some((flag) => flag.severity === 'high_alert');
}

export function sortFlags(flags) {
  return [...(Array.isArray(flags) ? flags : [])]
    .map(normalizeClinicalFlag)
    .filter(Boolean)
    .sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
}
